import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http, toastApiError, downloadUrl } from "../lib/api";
import { fmtTL, fmtDateTime } from "../lib/format";
import { Lock, ChevronDown, ChevronRight, Unlock, FileDown } from "lucide-react";
import { toast } from "../lib/notify";
import { useAuth } from "../context/AuthContext";

const KIND = { invoice: { label: "Fatura", to: "/veri-girisi?bolum=fatura" }, check: { label: "Çek", to: "/veri-girisi?bolum=cek" }, card: { label: "Ekstre", to: "/veri-girisi?bolum=kredi&alt=kart" }, loan: { label: "Kredi", to: "/veri-girisi?bolum=kredi" } };
const num = (v) => (v ? fmtTL(v) : "-");
// kayda gidiş: ESC ile aynı fiili bütçe satırı açık olarak geri dönülür
const recordLink = (kind, id, itemId) => `${KIND[kind]?.to || "/"}&kayit=${id}&don=${encodeURIComponent(`/butce?odak=actual-${itemId}`)}`;

export default function ActualBudget({ month, onChange }) {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState({});
  const load = useCallback(() => http.get(`/actual-budgets/${month}`).then((r) => setDoc(r.data)).catch(() => setDoc(null)), [month]);
  useEffect(() => { load(); }, [load]);
  // kayıttan ESC ile dönüş (?odak=actual-<kalem>): satırı aç, kaydır, yanıp söndür
  const odak = params.get("odak");
  useEffect(() => {
    if (!odak || !odak.startsWith("actual-") || !doc?.items) return;
    const itemId = odak.slice(7);
    setOpen((s) => ({ ...s, [itemId]: true }));
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-testid="actual-row-${itemId}"]`);
      if (row) { row.scrollIntoView({ behavior: "smooth", block: "center" }); row.classList.add("flash-row"); setTimeout(() => row.classList.remove("flash-row"), 4500); }
      setParams((p) => { const n = new URLSearchParams(p); n.delete("odak"); return n; }, { replace: true });
    }, 600);
    return () => clearTimeout(t);
  }, [odak, doc, setParams]);
  const closed = doc && doc.closed !== false && doc.items;
  const today = new Date().toISOString().slice(0, 7);

  const closeNow = async () => {
    if (!window.confirm(`${month} ayı Fiili Bütçe olarak kapatılsın mı?`)) return;
    try { await http.post(`/actual-budgets/${month}/close`); toast.success("Fiili bütçe oluşturuldu"); load(); onChange?.(); } catch (e) { toastApiError(e); }
  };
  const reopen = async () => {
    if (!window.confirm("Fiili bütçe kaydı silinip ay yeniden açılsın mı?")) return;
    try { await http.delete(`/actual-budgets/${month}`); toast.success("Ay yeniden açıldı"); load(); onChange?.(); } catch (e) { toastApiError(e); }
  };

  if (!closed) {
    if (month < today && isAdmin) {
      return (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-3" data-testid="actual-budget-pending">
          <span>Bu ay bitti ancak Fiili Bütçe henüz oluşmadı (ayın son günü gece 00:05'te otomatik oluşur).</span>
          <button type="button" onClick={closeNow} data-testid="actual-budget-close-now" className="ml-auto px-2.5 py-1 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700">Şimdi Kapat</button>
        </div>
      );
    }
    return null;
  }
  const t = doc.totals || {};
  return (
    <div className="border-b border-slate-200 bg-emerald-50/40" data-testid="actual-budget-panel">
      <div className="px-5 py-2.5 flex items-center gap-3 flex-wrap text-xs">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-emerald-700 text-white font-bold uppercase tracking-wide"><Lock className="w-3 h-3" /> Fiili Bütçe — {month}</span>
        <span className="text-slate-600">Ay kapandı: {fmtDateTime(doc.closed_at)} · Plan Gelir <b className="text-emerald-700">{num(t.plan_income)}</b> · Fiili Gelir <b className="text-emerald-700">{num(t.actual_income)}</b> · Plan Gider <b className="text-red-700">{num(t.plan_expense)}</b> · Fiili Gider <b className="text-red-700">{num(t.actual_expense)}</b></span>
        {isAdmin && <button type="button" onClick={reopen} data-testid="actual-budget-reopen" className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"><Unlock className="w-3 h-3" /> Ayı Yeniden Aç</button>}
        <a href={downloadUrl(`/actual-budgets/${month}/excel`)} target="_blank" rel="noreferrer" data-testid="actual-budget-excel"
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-700 text-white font-semibold hover:bg-emerald-800 ${isAdmin ? "" : "ml-auto"}`}><FileDown className="w-3.5 h-3.5" /> Excel İndir</a>
      </div>
      <div className="px-5 pb-3 overflow-x-auto">
        <table className="tbl text-[11px] w-full" data-testid="actual-budget-table">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-600">
            <th className="text-left px-2 py-1.5">Kalem</th><th className="text-center px-2 py-1.5">Plan Günü</th>
            <th className="text-right px-2 py-1.5 text-emerald-700">Plan Gelir</th><th className="text-right px-2 py-1.5 text-emerald-700">Fiili Gelir</th><th className="text-right px-2 py-1.5">Gelir Farkı</th>
            <th className="text-right px-2 py-1.5 text-red-700">Plan Gider</th><th className="text-right px-2 py-1.5 text-red-700">Fiili Gider</th><th className="text-right px-2 py-1.5">Gider Farkı</th><th className="px-2 py-1.5 text-center">Girişler</th>
          </tr></thead>
          <tbody>
            {doc.items.map((r) => {
              const o = !!open[r.budget_item_id];
              return (
                <FragmentRow key={r.budget_item_id} r={r} o={o} toggle={() => setOpen((s) => ({ ...s, [r.budget_item_id]: !s[r.budget_item_id] }))} />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({ r, o, toggle }) {
  return (
    <>
      <tr className="border-t border-slate-100 cursor-pointer hover:bg-white" onClick={toggle} data-testid={`actual-row-${r.budget_item_id}`}>
        <td className="px-2 py-1"><span className="inline-flex items-center gap-1">{o ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}{r.name}</span><span className="block text-[9px] text-slate-400 pl-4">{r.parent_name}</span></td>
        <td className="px-2 py-1 text-center font-mono">{r.planned_day ? `${r.planned_day}.` : "-"}</td>
        <td className="numeric-cell px-2 py-1 text-emerald-700">{num(r.plan_income)}</td>
        <td className="numeric-cell px-2 py-1 text-emerald-700">{num(r.actual_income)}</td>
        <td className="numeric-cell px-2 py-1">{r.plan_income || r.actual_income ? fmtTL(r.actual_income - r.plan_income) : "-"}</td>
        <td className="numeric-cell px-2 py-1 text-red-700">{num(r.plan_expense)}</td>
        <td className="numeric-cell px-2 py-1 text-red-700">{num(r.actual_expense)}</td>
        <td className="numeric-cell px-2 py-1">{r.plan_expense || r.actual_expense ? fmtTL(r.plan_expense - r.actual_expense) : "-"}</td>
        <td className="px-2 py-1 text-center text-slate-500">{r.records?.length || 0}</td>
      </tr>
      {o && (
        <tr className="bg-white"><td colSpan={9} className="px-6 py-2" data-testid={`actual-records-${r.budget_item_id}`}>
          {r.records?.length ? (
            <div className="flex flex-col gap-1">
              {r.records.map((x, i) => (
                <Link key={x.id || i} to={recordLink(x.kind, x.id, r.budget_item_id)} className="inline-flex items-center gap-1.5 text-[11px] text-sky-700 hover:underline w-fit">
                  <span className="font-mono text-slate-500">{x.day}. gün</span>
                  <span className="px-1 py-0.5 rounded bg-sky-100 text-sky-800 text-[9px] font-bold uppercase">{KIND[x.kind]?.label || x.kind}</span>{x.label}
                </Link>
              ))}
            </div>
          ) : <span className="text-[11px] text-slate-400">Bu kalem için giriş yok (yalnız plan)</span>}
        </td></tr>
      )}
    </>
  );
}
